import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { supabase } from "@/shared/integrations/supabase/client";

/**
 * Resolve the current client (Supabase `projects.id`) from the user's
 * `organization_id` custom claim.
 *
 * The claim carries the Firebase org id, which equals Supabase `projects.ref_id`.
 * Each login maps to exactly one client. Returns null when not signed in, the
 * claim is missing, or the org has no Supabase row yet.
 *
 * Shared by `useClientId` and `useProjects` so client identity is always the
 * claim's org — never derived from properties' (possibly null) org linkage.
 */
export async function resolveClientId(): Promise<string | null> {
  const fbUser = firebaseAuth?.currentUser;
  if (!fbUser) return null;

  const token = await fbUser.getIdTokenResult();
  const orgId = token.claims.organization_id as string | undefined;
  if (!orgId) return null;

  const { data, error } = await supabase
    .from("projects")
    .select("id")
    .eq("ref_id", orgId)
    .maybeSingle();
  if (error) throw error;

  return data?.id ?? null;
}
